import { addMonths, addYears, format, isAfter, parseISO, startOfDay } from 'date-fns';
import type { CanonicalBudgetCategory, ExpenseRecurringPeriod } from '@/types';
import {
  buildBankFeedAnalysis,
  buildFinanceStatementImportReview,
  detectFinanceStatementImportAdapter,
  type FinanceStatementImportAdapter,
  type FinanceStatementImportReview,
  inferMoneyCategory,
  resolveStatementImportLedgerSource,
  type FinanceLedgerIngestionSource,
} from '../../ui-foundation/features/money';

export type StatementTransaction = {
  date: string;
  description: string;
  amount: number;
  direction: 'in' | 'out';
  category: CanonicalBudgetCategory;
  confidence: 'high' | 'medium' | 'low';
};

export type RecurringOutgoingHint = {
  merchant: string;
  category: CanonicalBudgetCategory;
  count: number;
  averageAmount: number;
  totalAmount: number;
};

export type StatementImportSummary = {
  fileName: string;
  source?: FinanceLedgerIngestionSource;
  adapter?: FinanceStatementImportAdapter;
  review?: FinanceStatementImportReview;
  periodLabel: string;
  outgoingsTotal: number;
  incomingTotal: number;
  transactionCount: number;
  suggestedBudget: Record<CanonicalBudgetCategory, number>;
  confidenceCounts: Record<StatementTransaction['confidence'], number>;
  recurringOutgoings: RecurringOutgoingHint[];
};

export type RecurringPaymentStatus = 'upcoming' | 'due-soon' | 'missed' | 'unknown';

type ParsedRow = Record<string, string>;
type ParsedDelimitedTable = {
  headers: string[];
  rows: ParsedRow[];
};
type ParsedAmountCell = {
  amount: number;
  direction: StatementTransaction['direction'] | null;
};

const CSV_DATE_HEADERS = ['date', 'transaction_date', 'posted_date', 'booking_date', 'posted', 'transactiondate'];
const CSV_DESCRIPTION_HEADERS = ['description', 'details', 'reference', 'payee', 'transaction', 'narrative', 'transaction_description'];
const CSV_AMOUNT_HEADERS = ['amount', 'value', 'transaction_amount', 'amount_gbp'];
const CSV_DEBIT_HEADERS = ['debit', 'money_out', 'withdrawal', 'paid_out'];
const CSV_CREDIT_HEADERS = ['credit', 'money_in', 'deposit', 'paid_in'];
const CSV_TYPE_HEADERS = ['type', 'transaction_type', 'dr_cr', 'debit_credit'];

function normaliseHeader(value: string) {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
}

function splitDelimitedLine(line: string, delimiter: string) {
  const values: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (inQuotes && line[index + 1] === '"') {
        current += '"';
        index += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  values.push(current.trim());
  return values;
}

function detectDelimitedTextSeparator(lines: string[]) {
  const separators = [',', ';', '\t'];
  let bestSeparator = ',';
  let bestScore = -1;

  for (const separator of separators) {
    const score = lines
      .slice(0, 3)
      .reduce((sum, line) => sum + Math.max(splitDelimitedLine(line, separator).length - 1, 0), 0);

    if (score > bestScore) {
      bestScore = score;
      bestSeparator = separator;
    }
  }

  return bestSeparator;
}

function parseCsv(text: string): ParsedDelimitedTable {
  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length < 2) {
    return {
      headers: [],
      rows: [],
    };
  }

  const delimiter = detectDelimitedTextSeparator(lines);
  const headers = splitDelimitedLine(lines[0], delimiter).map(normaliseHeader);
  const rows = lines.slice(1).map((line) => {
    const values = splitDelimitedLine(line, delimiter);
    return headers.reduce<ParsedRow>((row, header, index) => {
      row[header] = values[index] || '';
      return row;
    }, {});
  });

  return {
    headers,
    rows,
  };
}

function parseNumber(value: string) {
  const cleaned = value.replace(/[£,\s]/g, '').replace(/[()]/g, '');
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
}

function roundCurrency(value: number) {
  return Math.round(value * 100) / 100;
}

function pickFirstRowValue(row: ParsedRow, keys: string[]) {
  for (const key of keys) {
    const value = row[key];
    if (value != null && value !== '') {
      return value;
    }
  }

  return '';
}

function parseStatementAmountCell(value: string | null | undefined): ParsedAmountCell {
  const raw = (value || '').trim();
  if (!raw) {
    return {
      amount: 0,
      direction: null,
    };
  }

  const amount = Math.abs(roundCurrency(parseNumber(raw.replace(/\b(?:CR|DR)\b/gi, '').replace(/[+-]/g, ''))));
  if (amount <= 0) {
    return {
      amount: 0,
      direction: null,
    };
  }

  if (/\bCR\b/i.test(raw) || raw.startsWith('+')) {
    return { amount, direction: 'in' };
  }

  if (/\bDR\b/i.test(raw) || raw.startsWith('-') || /^\(.*\)$/.test(raw)) {
    return { amount, direction: 'out' };
  }

  return {
    amount,
    direction: null,
  };
}

function inferDirectionFromTypeHint(typeHint: string | null | undefined) {
  const normalised = (typeHint || '').trim().toLowerCase();
  if (!normalised) return null;

  if (/(credit|cr|deposit|paid[_\s-]?in|money[_\s-]?in|income)/.test(normalised)) {
    return 'in' as const;
  }

  if (/(debit|dr|withdrawal|paid[_\s-]?out|money[_\s-]?out|payment)/.test(normalised)) {
    return 'out' as const;
  }

  return null;
}

function buildStatementTransaction(
  date: string,
  description: string,
  amount: number,
  direction: StatementTransaction['direction'],
) {
  const { category, confidence } = inferMoneyCategory(description);
  return {
    date,
    description,
    amount,
    direction,
    category,
    confidence,
  };
}

function toIsoDate(value: string) {
  if (!value) return '';

  const parts = value.split(/[/-]/).map((part) => part.trim());
  if (parts.length === 3) {
    const [first, second, third] = parts;
    if (first.length === 4) {
      return `${first}-${second.padStart(2, '0')}-${third.padStart(2, '0')}`;
    }

    if (third.length === 4) {
      const day = first.padStart(2, '0');
      const month = second.padStart(2, '0');
      return `${third}-${month}-${day}`;
    }
  }

  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) {
    return parsed.toISOString().slice(0, 10);
  }

  if (/^\d{1,2}\s+[A-Za-z]{3,9}$/i.test(value.trim())) {
    const now = new Date();
    const fallback = new Date(`${value} ${now.getFullYear()}`);
    if (!Number.isNaN(fallback.getTime())) {
      const futureThreshold = new Date(now);
      futureThreshold.setMonth(futureThreshold.getMonth() + 1);
      if (fallback > futureThreshold) {
        fallback.setFullYear(fallback.getFullYear() - 1);
      }
      return fallback.toISOString().slice(0, 10);
    }
  }

  return '';
}

type BuildStatementImportSummaryInput = {
  fileName: string;
  format: 'csv' | 'pdf';
  transactions: StatementTransaction[];
  sourceRowCount: number;
  skippedRowCount: number;
  textSample?: string;
  headerKeys?: string[];
};

function buildStatementImportSummary({
  fileName,
  format,
  transactions,
  sourceRowCount,
  skippedRowCount,
  textSample,
  headerKeys,
}: BuildStatementImportSummaryInput) {
  const months = Array.from(new Set(transactions.map((entry) => entry.date.slice(0, 7))));
  const summaryBase = summariseStatementTransactions(transactions);
  const adapter = detectFinanceStatementImportAdapter({
    format,
    fileName,
    textSample,
    headerKeys,
  });
  const review = buildFinanceStatementImportReview({
    adapter,
    parsedTransactionCount: transactions.length,
    sourceRowCount,
    skippedRowCount,
    lowConfidenceCount: transactions.filter((entry) => entry.confidence === 'low').length,
  });

  return {
    transactions,
    summary: {
      fileName,
      source: resolveStatementImportLedgerSource(fileName),
      adapter,
      review,
      periodLabel: months.length === 1 ? months[0] : months.join(', '),
      ...summaryBase,
    } satisfies StatementImportSummary,
  };
}

type PdfAmountMatch = {
  raw: string;
  value: number;
  index: number;
  direction: StatementTransaction['direction'] | null;
};

type PdfCandidateRow = {
  date: string;
  description: string;
  amount: number;
  direction: StatementTransaction['direction'] | null;
  balance: number | null;
};

const PDF_SKIP_PATTERNS = [
  /\bopening balance\b/i,
  /\bclosing balance\b/i,
  /\bbalance brought forward\b/i,
  /\bbalance carried forward\b/i,
  /\bavailable balance\b/i,
  /\bstatement date\b/i,
  /\bpage \d+\b/i,
  /\bsort code\b/i,
  /\baccount number\b/i,
  /\biban\b/i,
];

const PDF_DATE_PREFIX = /^(\d{1,2}(?:[/-]\d{1,2}(?:[/-]\d{2,4})?|\s+[A-Za-z]{3,9}(?:\s+\d{2,4})?))\s+(.+)$/i;
const PDF_AMOUNT_PATTERN = /(?:\(?[-+]?£?\d[\d,]*\.\d{2}\)?(?:\s?(?:CR|DR))?)/gi;
const PDF_INCOMING_HINTS = ['salary', 'payroll', 'wages', 'refund', 'benefit', 'interest', 'cashback', 'transfer from', 'credit', 'maintenance', 'child benefit', 'dwp uc', 'reversal of', 'bacs'];
const PDF_OUTGOING_HINTS = ['card payment', 'card purchase', 'contactless', 'direct debit', 'standing order', 'transfer to', 'debit', 'dd ', 'so ', 'bp ', 'mas ', ')))', 'bumper', 'clearpay'];
const FIRST_DIRECT_PREFIX_DIRECTIONS: Array<[RegExp, StatementTransaction['direction']]> = [
  [/^cr\b/i, 'in'],
  [/^dd\b/i, 'out'],
  [/^so\b/i, 'out'],
  [/^bp\b/i, 'out'],
  [/^mas\b/i, 'out'],
  [/^\)\)\)/, 'out'],
  [/^cash\b/i, 'out'],
  [/^atm\b/i, 'out'],
  [/^tfr\b/i, 'out'],
];

type FirstDirectPendingTransaction = {
  descriptionParts: string[];
  amount: number | null;
  direction: StatementTransaction['direction'] | null;
  balance: number | null;
};

type FirstDirectTransactionCandidate = {
  date: string;
  description: string;
  amount: number;
  direction: StatementTransaction['direction'] | null;
  balance: number | null;
};

function parsePdfAmountToken(raw: string): PdfAmountMatch | null {
  const trimmed = raw.trim();
  const amount = roundCurrency(parseNumber(trimmed.replace(/\b(?:CR|DR)\b/gi, '').replace(/[+-]/g, '')));
  if (amount < 0) return null;

  let direction: StatementTransaction['direction'] | null = null;
  if (/\bCR\b/i.test(trimmed) || trimmed.includes('+')) {
    direction = 'in';
  } else if (/\bDR\b/i.test(trimmed) || trimmed.includes('-') || /^\(.*\)$/.test(trimmed)) {
    direction = 'out';
  }

  return {
    raw: trimmed,
    value: amount,
    index: 0,
    direction,
  };
}

function inferPdfDirectionFromDescription(description: string) {
  const normalised = description.toLowerCase();
  if (PDF_INCOMING_HINTS.some((hint) => normalised.includes(hint))) {
    return 'in' as const;
  }

  if (PDF_OUTGOING_HINTS.some((hint) => normalised.includes(hint))) {
    return 'out' as const;
  }

  return null;
}

function parseBalanceValue(raw: string) {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const isDebitBalance = /\bD\b/i.test(trimmed) || /-\s*$/.test(trimmed);
  const numeric = parseNumber(trimmed.replace(/\b[DC]\b/gi, '').replace(/[+-]/g, ''));
  if (!Number.isFinite(numeric) || numeric <= 0) return null;
  return isDebitBalance ? -numeric : numeric;
}

function inferFirstDirectDirection(description: string) {
  for (const [pattern, direction] of FIRST_DIRECT_PREFIX_DIRECTIONS) {
    if (pattern.test(description)) {
      return direction;
    }
  }

  return inferPdfDirectionFromDescription(description);
}

function finaliseFirstDirectPendingTransaction(
  date: string,
  pending: FirstDirectPendingTransaction,
): FirstDirectTransactionCandidate | null {
  if (pending.amount == null || pending.amount <= 0) return null;

  const description = pending.descriptionParts
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!description) return null;

  return {
    date,
    description,
    amount: pending.amount,
    direction: pending.direction || inferFirstDirectDirection(description),
    balance: pending.balance,
  };
}

function createFirstDirectPendingTransaction(
  line: string,
  current: FirstDirectPendingTransaction | null,
): FirstDirectPendingTransaction | null {
  const amountMatches = Array.from(line.matchAll(PDF_AMOUNT_PATTERN))
    .map((match) => ({
      raw: match[0],
      index: match.index ?? 0,
      value: roundCurrency(parseNumber(match[0].replace(/\b(?:CR|DR)\b/gi, '').replace(/[+-]/g, ''))),
    }))
    .filter((entry) => entry.value > 0);

  if (amountMatches.length === 0) {
    const next = current || {
      descriptionParts: [],
      amount: null,
      direction: null,
      balance: null,
    };

    next.descriptionParts.push(line.trim());
    return next;
  }

  const next = current || {
    descriptionParts: [],
    amount: null,
    direction: null,
    balance: null,
  };
  const firstAmountIndex = amountMatches[0]?.index ?? line.length;
  const descriptionPart = line
    .slice(0, firstAmountIndex)
    .replace(/\s+/g, ' ')
    .trim();

  if (descriptionPart) {
    next.descriptionParts.push(descriptionPart);
  }

  const transactionAmount = amountMatches.length >= 2
    ? amountMatches[amountMatches.length - 2].value
    : amountMatches[0].value;
  const balanceAmount = amountMatches.length >= 2
    ? parseBalanceValue(amountMatches[amountMatches.length - 1].raw)
    : null;

  next.amount = transactionAmount;
  next.balance = balanceAmount;
  next.direction = inferFirstDirectDirection(next.descriptionParts.join(' '));

  return next;
}

function parseFirstDirectPdfText(text: string) {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const candidates: FirstDirectTransactionCandidate[] = [];
  let currentDate = '';
  let currentPending: FirstDirectPendingTransaction | null = null;
  let lastKnownBalance: number | null = null;

  const flushPending = () => {
    if (!currentDate || !currentPending) {
      currentPending = null;
      return;
    }

    const candidate = finaliseFirstDirectPendingTransaction(currentDate, currentPending);
    if (candidate) {
      candidates.push(candidate);
      if (candidate.balance != null) {
        lastKnownBalance = candidate.balance;
      }
    }
    currentPending = null;
  };

  for (const line of lines) {
    const dateMatch = line.match(PDF_DATE_PREFIX);
    if (dateMatch) {
      flushPending();
      currentDate = toIsoDate(dateMatch[1]);

      const remainder = dateMatch[2].replace(/\s+/g, ' ').trim();
      if (!currentDate || !remainder) continue;

      if (/^balance brought forward\b/i.test(remainder)) {
        const amountMatch = remainder.match(/£?\s*[\d,]+(?:\.\d{2})?(?:\s*[DC])?/i);
        lastKnownBalance = amountMatch ? parseBalanceValue(amountMatch[0]) : lastKnownBalance;
        continue;
      }

      currentPending = createFirstDirectPendingTransaction(remainder, null);
      if (currentPending?.amount != null) {
        flushPending();
      }
      continue;
    }

    if (/^balance carried forward\b/i.test(line)) {
      const amountMatch = line.match(/£?\s*[\d,]+(?:\.\d{2})?(?:\s*[DC])?/i);
      const balance = amountMatch ? parseBalanceValue(amountMatch[0]) : null;
      if (currentPending && balance != null) {
        currentPending.balance = balance;
      } else if (balance != null) {
        lastKnownBalance = balance;
      }
      flushPending();
      continue;
    }

    if (!currentDate || PDF_SKIP_PATTERNS.some((pattern) => pattern.test(line))) {
      continue;
    }

    const nextPending = createFirstDirectPendingTransaction(line, currentPending);
    if (nextPending?.amount != null) {
      currentPending = nextPending;
      flushPending();
      continue;
    }

    currentPending = nextPending;
  }

  flushPending();

  const transactions = candidates.flatMap((candidate) => {
    if (!candidate.direction || candidate.amount <= 0) return [];
    return [buildStatementTransaction(candidate.date, candidate.description, candidate.amount, candidate.direction)];
  });

  return {
    transactions,
    sourceRowCount: candidates.length,
    skippedRowCount: Math.max(candidates.length - transactions.length, 0),
    lastKnownBalance,
  };
}

function parsePdfCandidateRow(line: string): PdfCandidateRow | null {
  if (!line || PDF_SKIP_PATTERNS.some((pattern) => pattern.test(line))) {
    return null;
  }

  const dateMatch = line.match(PDF_DATE_PREFIX);
  if (!dateMatch) return null;

  const date = toIsoDate(dateMatch[1]);
  if (!date) return null;

  const remainder = dateMatch[2].replace(/\s+/g, ' ').trim();
  const amountMatches = Array.from(remainder.matchAll(PDF_AMOUNT_PATTERN))
    .map((match) => {
      const parsed = parsePdfAmountToken(match[0]);
      if (!parsed) return null;
      return {
        ...parsed,
        index: match.index ?? 0,
      };
    })
    .filter((entry): entry is PdfAmountMatch => Boolean(entry));

  if (amountMatches.length === 0) return null;

  const firstAmountIndex = amountMatches[0]?.index ?? remainder.length;
  const description = remainder
    .slice(0, firstAmountIndex)
    .replace(/\s+/g, ' ')
    .replace(/[–—-]\s*$/, '')
    .trim();

  if (!description) return null;

  const explicitMatch = amountMatches.find((entry) => entry.direction);
  if (explicitMatch) {
    return {
      date,
      description,
      amount: explicitMatch.value,
      direction: explicitMatch.direction,
      balance: amountMatches.length > 1 ? amountMatches[amountMatches.length - 1].value : null,
    };
  }

  if (amountMatches.length >= 3) {
    const debitColumn = amountMatches[amountMatches.length - 3];
    const creditColumn = amountMatches[amountMatches.length - 2];
    const balanceColumn = amountMatches[amountMatches.length - 1];
    const debitValue = debitColumn.value;
    const creditValue = creditColumn.value;

    if (debitValue > 0 && creditValue === 0) {
      return {
        date,
        description,
        amount: debitValue,
        direction: 'out',
        balance: balanceColumn.value,
      };
    }

    if (creditValue > 0 && debitValue === 0) {
      return {
        date,
        description,
        amount: creditValue,
        direction: 'in',
        balance: balanceColumn.value,
      };
    }
  }

  if (amountMatches.length >= 2) {
    return {
      date,
      description,
      amount: amountMatches[amountMatches.length - 2].value,
      direction: inferPdfDirectionFromDescription(description),
      balance: amountMatches[amountMatches.length - 1].value,
    };
  }

  return {
    date,
    description,
    amount: amountMatches[0].value,
    direction: inferPdfDirectionFromDescription(description),
    balance: null,
  };
}

function resolvePdfDirectionFromBalances(
  row: PdfCandidateRow,
  index: number,
  rows: PdfCandidateRow[],
  order: 'asc' | 'desc',
) {
  if (row.direction || row.balance === null) return row.direction;

  const comparisonRow = order === 'asc' ? rows[index - 1] : rows[index + 1];
  if (!comparisonRow || comparisonRow.balance === null) {
    return row.direction;
  }

  const balanceDelta = roundCurrency(row.balance - comparisonRow.balance);
  if (Math.abs(Math.abs(balanceDelta) - row.amount) <= 0.05) {
    return balanceDelta >= 0 ? 'in' : 'out';
  }

  return row.direction;
}

export function getNextRecurringExpenseDate(
  expenseDate: string,
  recurringPeriod: ExpenseRecurringPeriod,
  referenceDate = new Date(),
) {
  if (!expenseDate || recurringPeriod === 'none') return null;

  let nextDue = parseISO(expenseDate);
  if (Number.isNaN(nextDue.getTime())) return null;

  const reference = startOfDay(referenceDate);
  if (isAfter(nextDue, reference)) {
    return format(nextDue, 'yyyy-MM-dd');
  }

  while (!isAfter(nextDue, reference)) {
    nextDue = recurringPeriod === 'monthly' ? addMonths(nextDue, 1) : addYears(nextDue, 1);
  }

  return format(nextDue, 'yyyy-MM-dd');
}

export function getDefaultRecurringReminderDays(recurringPeriod: ExpenseRecurringPeriod) {
  if (recurringPeriod === 'yearly') return 30;
  if (recurringPeriod === 'monthly') return 3;
  return 0;
}

export function getRecurringPaymentStatus(
  nextDueDate: string | null | undefined,
  reminderDaysBefore: number,
  referenceDate = new Date(),
) {
  if (!nextDueDate) {
    return {
      status: 'unknown' as RecurringPaymentStatus,
      daysUntilDue: null,
    };
  }

  const dueDate = parseISO(nextDueDate);
  if (Number.isNaN(dueDate.getTime())) {
    return {
      status: 'unknown' as RecurringPaymentStatus,
      daysUntilDue: null,
    };
  }

  const daysUntilDue = Math.round(
    (startOfDay(dueDate).getTime() - startOfDay(referenceDate).getTime()) / (1000 * 60 * 60 * 24),
  );

  if (daysUntilDue < 0) {
    return {
      status: 'missed' as RecurringPaymentStatus,
      daysUntilDue,
    };
  }

  if (daysUntilDue <= reminderDaysBefore) {
    return {
      status: 'due-soon' as RecurringPaymentStatus,
      daysUntilDue,
    };
  }

  return {
    status: 'upcoming' as RecurringPaymentStatus,
    daysUntilDue,
  };
}

export function summariseStatementTransactions(transactions: StatementTransaction[]) {
  const outgoings = transactions.filter((entry) => entry.direction === 'out');
  const incoming = transactions.filter((entry) => entry.direction === 'in');
  const analysis = buildBankFeedAnalysis(
    transactions.map((entry) => ({
      bookingDate: entry.date,
      amount: entry.direction === 'out' ? -entry.amount : entry.amount,
      description: entry.description,
    })),
  );

  const confidenceCounts = outgoings.reduce<Record<StatementTransaction['confidence'], number>>((counts, entry) => {
    counts[entry.confidence] += 1;
    return counts;
  }, {
    high: 0,
    medium: 0,
    low: 0,
  });

  const recurringOutgoings: RecurringOutgoingHint[] = analysis.recurringOutgoings
    .slice(0, 5)
    .map((entry) => ({
      merchant: entry.merchant,
      category: entry.category,
      count: entry.count,
      totalAmount: entry.totalAmount,
      averageAmount: entry.averageAmount,
    }));

  return {
    outgoingsTotal: analysis.outgoingTotal || outgoings.reduce((sum, entry) => sum + entry.amount, 0),
    incomingTotal: analysis.incomingTotal || incoming.reduce((sum, entry) => sum + entry.amount, 0),
    transactionCount: transactions.length,
    suggestedBudget: analysis.suggestedBudget as Record<CanonicalBudgetCategory, number>,
    confidenceCounts,
    recurringOutgoings,
  };
}

export function buildCategorisedStatementCsv(transactions: StatementTransaction[]) {
  const rows = [
    ['Date', 'Description', 'Direction', 'Amount', 'Category', 'Confidence'],
    ...transactions.map((transaction) => [
      transaction.date,
      `"${transaction.description.replace(/"/g, '""')}"`,
      transaction.direction,
      transaction.amount.toFixed(2),
      transaction.category,
      transaction.confidence,
    ]),
  ];

  return rows.map((row) => row.join(',')).join('\n');
}

export function parseStatementCsv(text: string, fileName: string) {
  const parsedTable = parseCsv(text);
  const transactions: StatementTransaction[] = [];
  const adapter = detectFinanceStatementImportAdapter({
    format: 'csv',
    fileName,
    textSample: text.slice(0, 2000),
    headerKeys: parsedTable.headers,
  });

  for (const row of parsedTable.rows) {
    const date = toIsoDate(pickFirstRowValue(row, CSV_DATE_HEADERS));
    const description = pickFirstRowValue(row, CSV_DESCRIPTION_HEADERS) || 'Statement transaction';
    const amountCell = parseStatementAmountCell(pickFirstRowValue(row, CSV_AMOUNT_HEADERS));
    const debitCell = parseStatementAmountCell(pickFirstRowValue(row, CSV_DEBIT_HEADERS));
    const creditCell = parseStatementAmountCell(pickFirstRowValue(row, CSV_CREDIT_HEADERS));
    const typeHintDirection = inferDirectionFromTypeHint(pickFirstRowValue(row, CSV_TYPE_HEADERS));

    let direction: StatementTransaction['direction'] | null = null;
    let amount = 0;

    if (debitCell.amount > 0) {
      direction = 'out';
      amount = debitCell.amount;
    } else if (creditCell.amount > 0) {
      direction = 'in';
      amount = creditCell.amount;
    } else if (amountCell.amount > 0 && amountCell.direction) {
      direction = amountCell.direction;
      amount = amountCell.amount;
    } else if (amountCell.amount > 0 && typeHintDirection) {
      direction = typeHintDirection;
      amount = amountCell.amount;
    } else if (amountCell.amount > 0) {
      const rawAmount = parseNumber(pickFirstRowValue(row, CSV_AMOUNT_HEADERS));
      direction = rawAmount < 0 ? 'out' : 'in';
      amount = amountCell.amount;
    }

    if (!date || !description || !direction || amount <= 0) continue;

    transactions.push(buildStatementTransaction(date, description, amount, direction));
  }

  return buildStatementImportSummary({
    fileName,
    format: 'csv',
    transactions,
    sourceRowCount: parsedTable.rows.length,
    skippedRowCount: Math.max(parsedTable.rows.length - transactions.length, 0),
    textSample: text.slice(0, 2000),
    headerKeys: parsedTable.headers,
  });
}

export function parseStatementPdfText(text: string, fileName: string) {
  const adapter = detectFinanceStatementImportAdapter({
    format: 'pdf',
    fileName,
    textSample: text.slice(0, 4000),
  });

  if (adapter.id === 'first-direct-pdf') {
    const parsed = parseFirstDirectPdfText(text);
    return buildStatementImportSummary({
      fileName,
      format: 'pdf',
      transactions: parsed.transactions,
      sourceRowCount: parsed.sourceRowCount,
      skippedRowCount: parsed.skippedRowCount,
      textSample: text.slice(0, 4000),
    });
  }

  const lines = text
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const candidateRows = lines
    .map((line) => parsePdfCandidateRow(line))
    .filter((row): row is PdfCandidateRow => Boolean(row));

  let ascendingCount = 0;
  let descendingCount = 0;
  for (let index = 1; index < candidateRows.length; index += 1) {
    const previousDate = candidateRows[index - 1].date;
    const currentDate = candidateRows[index].date;
    if (currentDate > previousDate) ascendingCount += 1;
    if (currentDate < previousDate) descendingCount += 1;
  }

  const order = descendingCount > ascendingCount ? 'desc' : 'asc';
  const transactions = candidateRows.flatMap((row, index, rows) => {
    const direction = resolvePdfDirectionFromBalances(row, index, rows, order);
    if (!direction || row.amount <= 0) return [];
    return [buildStatementTransaction(row.date, row.description, row.amount, direction)];
  });

  return buildStatementImportSummary({
    fileName,
    format: 'pdf',
    transactions,
    sourceRowCount: candidateRows.length,
    skippedRowCount: Math.max(candidateRows.length - transactions.length, 0),
    textSample: text.slice(0, 4000),
  });
}

export type DebtModel = {
  monthlyInterest: number;
  annualInterest: number;
  estimatedMonthsToClear: number | null;
};

export type DebtStrategyInput = {
  id: string;
  balance: number;
  apr: number | null;
  minimumPayment: number | null;
};

export type DebtStrategyName = 'avalanche' | 'snowball';

export type DebtStrategyResult = {
  strategy: DebtStrategyName;
  monthlyBudget: number;
  totalInterestPaid: number;
  monthsToClear: number | null;
};

export type DebtStrategyComparison = {
  avalanche: DebtStrategyResult;
  snowball: DebtStrategyResult;
  winner: DebtStrategyName | 'tie';
  interestDelta: number;
  monthsDelta: number;
};

export type DebtRateBenchmarkKind = 'credit-card' | 'personal-loan' | 'overdraft';

export type DebtRateComparison = {
  kind: DebtRateBenchmarkKind;
  benchmarkLabel: string;
  currentApr: number;
  benchmarkApr: number;
  differenceApr: number;
  periodLabel: string;
  status: 'below-average' | 'around-average' | 'above-average' | 'well-above-average';
};

export type DebtPressureInput = {
  debtType: string;
  balance: number;
  apr: number | null;
  minimumPayment: number | null;
  paymentStatus: 'current' | 'struggling' | 'missed';
};

export type DebtPressureAssessment = {
  level: 'watch' | 'high' | 'critical';
  title: string;
  summary: string;
  reasons: string[];
  minimumPaymentShare: number | null;
  flaggedDebtCount: number;
  missedDebtCount: number;
  highCostDebtCount: number;
};

export type BalanceTransferEstimate = {
  currentCarryCost: number;
  promoCarryCost: number;
  transferFeeCost: number;
  transferTotalCost: number;
  estimatedSaving: number;
};

export type BudgetPressureAssessment = {
  level: 'watch' | 'high' | 'critical';
  title: string;
  summary: string;
  reasons: string[];
  monthlyIncome: number | null;
  committedSpend: number;
  committedShare: number | null;
  overBudgetCategoryCount: number;
  missedRecurringCount: number;
  dueSoonRecurringCount: number;
};

export type SurvivalActivationAssessment = {
  level: 'watch' | 'high' | 'critical';
  title: string;
  summary: string;
  reasons: string[];
  trigger: 'budget' | 'debt' | 'both';
  budgetPressure: BudgetPressureAssessment | null;
  debtPressure: DebtPressureAssessment | null;
};

const DEBT_RATE_BENCHMARKS: Record<DebtRateBenchmarkKind, { label: string; apr: number; periodLabel: string }> = {
  'credit-card': {
    label: 'Credit card',
    apr: 21.75,
    periodLabel: 'Bank of England Jan 2026',
  },
  'personal-loan': {
    label: 'Personal loan',
    apr: 9.03,
    periodLabel: 'Bank of England Jan 2026',
  },
  overdraft: {
    label: 'Overdraft',
    apr: 22.03,
    periodLabel: 'Bank of England Jan 2026',
  },
};

function getDebtRateBenchmarkKind(debtType: string): DebtRateBenchmarkKind | null {
  const normalised = debtType.trim().toLowerCase();
  if (!normalised) return null;

  if (normalised.includes('overdraft')) return 'overdraft';
  if (normalised.includes('card')) return 'credit-card';
  if (normalised.includes('loan')) return 'personal-loan';

  return null;
}

function calculateCarryCost(balance: number, apr: number, months: number) {
  const safeBalance = Math.max(balance, 0);
  const safeApr = Math.max(apr, 0);
  const safeMonths = Math.max(Math.round(months), 0);
  const monthlyRate = safeApr / 100 / 12;

  let runningBalance = safeBalance;
  let totalInterest = 0;
  for (let index = 0; index < safeMonths; index += 1) {
    const interest = runningBalance * monthlyRate;
    runningBalance += interest;
    totalInterest += interest;
  }

  return totalInterest;
}

export function getDebtRateComparison(debtType: string, apr: number | null | undefined): DebtRateComparison | null {
  if (apr == null || !Number.isFinite(apr) || apr <= 0) return null;

  const kind = getDebtRateBenchmarkKind(debtType);
  if (!kind) return null;

  const benchmark = DEBT_RATE_BENCHMARKS[kind];
  const differenceApr = apr - benchmark.apr;

  let status: DebtRateComparison['status'] = 'around-average';
  if (differenceApr >= 5) {
    status = 'well-above-average';
  } else if (differenceApr >= 2) {
    status = 'above-average';
  } else if (differenceApr <= -2) {
    status = 'below-average';
  }

  return {
    kind,
    benchmarkLabel: benchmark.label,
    currentApr: apr,
    benchmarkApr: benchmark.apr,
    differenceApr,
    periodLabel: benchmark.periodLabel,
    status,
  };
}

export function assessDebtPressure(
  debts: DebtPressureInput[],
  annualIncome: number,
): DebtPressureAssessment | null {
  if (debts.length === 0) return null;

  const monthlyIncome = annualIncome > 0 ? annualIncome / 12 : null;
  const totalMinimums = debts.reduce((sum, debt) => sum + Math.max(debt.minimumPayment || 0, 0), 0);
  const minimumPaymentShare = monthlyIncome && monthlyIncome > 0 ? totalMinimums / monthlyIncome : null;
  const flaggedDebtCount = debts.filter((debt) => debt.paymentStatus !== 'current').length;
  const missedDebtCount = debts.filter((debt) => debt.paymentStatus === 'missed').length;
  const highCostDebtCount = debts.filter((debt) => {
    const comparison = getDebtRateComparison(debt.debtType, debt.apr);
    return comparison?.status === 'above-average' || comparison?.status === 'well-above-average';
  }).length;

  const reasons: string[] = [];
  if (minimumPaymentShare !== null && minimumPaymentShare >= 0.15) {
    reasons.push(`Minimum payments already take about ${(minimumPaymentShare * 100).toFixed(0)}% of your estimated monthly income.`);
  }
  if (flaggedDebtCount > 0) {
    reasons.push(`${flaggedDebtCount} debt${flaggedDebtCount === 1 ? ' is' : 's are'} already marked missed or struggling.`);
  }
  if (highCostDebtCount > 0) {
    reasons.push(`${highCostDebtCount} account${highCostDebtCount === 1 ? ' is' : 's are'} priced above the latest Bank of England average for that debt type.`);
  }
  if (reasons.length === 0) {
    reasons.push('You still have space to act early before the position gets more reactive.');
  }

  if (
    (minimumPaymentShare !== null && minimumPaymentShare >= 0.25) ||
    missedDebtCount >= 2 ||
    (missedDebtCount >= 1 && flaggedDebtCount >= 2)
  ) {
    return {
      level: 'critical',
      title: 'Regulated debt help should be your first move',
      summary: 'Your saved debt picture is already showing crisis markers. Keep Adulting for scripts and prep, but bring a regulated debt adviser in now.',
      reasons,
      minimumPaymentShare,
      flaggedDebtCount,
      missedDebtCount,
      highCostDebtCount,
    };
  }

  if (
    (minimumPaymentShare !== null && minimumPaymentShare >= 0.15) ||
    flaggedDebtCount >= 2 ||
    missedDebtCount >= 1 ||
    highCostDebtCount >= 2
  ) {
    return {
      level: 'high',
      title: 'Debt pressure is building',
      summary: 'This still looks salvageable, but it needs a clearer plan now rather than another month of hoping the numbers calm down on their own.',
      reasons,
      minimumPaymentShare,
      flaggedDebtCount,
      missedDebtCount,
      highCostDebtCount,
    };
  }

  return {
    level: 'watch',
    title: 'Act early while you still have room',
    summary: 'Nothing here forces crisis support yet, but checking rates and options now is usually much easier than waiting for the first miss.',
    reasons,
    minimumPaymentShare,
    flaggedDebtCount,
    missedDebtCount,
    highCostDebtCount,
  };
}

export function calculateBalanceTransferEstimate(
  balance: number,
  currentApr: number | null | undefined,
  promoApr: number,
  transferFeePercent: number,
  promoMonths: number,
): BalanceTransferEstimate | null {
  if (!Number.isFinite(balance) || balance <= 0) return null;
  if (currentApr == null || !Number.isFinite(currentApr) || currentApr <= 0) return null;
  if (!Number.isFinite(promoApr) || promoApr < 0) return null;
  if (!Number.isFinite(transferFeePercent) || transferFeePercent < 0) return null;
  if (!Number.isFinite(promoMonths) || promoMonths <= 0) return null;

  const currentCarryCost = calculateCarryCost(balance, currentApr, promoMonths);
  const promoCarryCost = calculateCarryCost(balance, promoApr, promoMonths);
  const transferFeeCost = balance * (transferFeePercent / 100);
  const transferTotalCost = promoCarryCost + transferFeeCost;

  return {
    currentCarryCost,
    promoCarryCost,
    transferFeeCost,
    transferTotalCost,
    estimatedSaving: currentCarryCost - transferTotalCost,
  };
}

export function assessBudgetPressure({
  annualIncome,
  totalSpent,
  recurringMonthlyCommitment,
  overBudgetCategoryCount,
  missedRecurringCount,
  dueSoonRecurringCount,
}: {
  annualIncome: number;
  totalSpent: number;
  recurringMonthlyCommitment: number;
  overBudgetCategoryCount: number;
  missedRecurringCount: number;
  dueSoonRecurringCount: number;
}): BudgetPressureAssessment | null {
  const monthlyIncome = annualIncome > 0 ? annualIncome / 12 : null;
  const committedSpend = Math.max(totalSpent, 0) + Math.max(recurringMonthlyCommitment, 0);
  const committedShare = monthlyIncome && monthlyIncome > 0 ? committedSpend / monthlyIncome : null;
  const reasons: string[] = [];

  if (committedShare !== null && committedShare >= 0.75) {
    reasons.push(
      `Logged spend plus recurring commitments are already using about ${(committedShare * 100).toFixed(0)}% of your monthly income.`,
    );
  }
  if (overBudgetCategoryCount > 0) {
    reasons.push(
      `${overBudgetCategoryCount} budget categor${overBudgetCategoryCount === 1 ? 'y is' : 'ies are'} already over the current monthly limit.`,
    );
  }
  if (missedRecurringCount > 0) {
    reasons.push(
      `${missedRecurringCount} recurring payment${missedRecurringCount === 1 ? ' is' : 's are'} already marked missed.`,
    );
  }
  if (dueSoonRecurringCount >= 2) {
    reasons.push(
      `${dueSoonRecurringCount} recurring payments are due soon, so the next few days need a clearer plan.`,
    );
  }

  if (reasons.length === 0) return null;

  if (
    (committedShare !== null && committedShare >= 1) ||
    missedRecurringCount >= 2 ||
    ((committedShare !== null && committedShare >= 0.9) && overBudgetCategoryCount >= 2)
  ) {
    return {
      level: 'critical',
      title: 'Budget pressure is already in survival territory',
      summary: 'The money picture is no longer just tight. Use Survival now for the next practical steps before another bill or arrears letter lands.',
      reasons,
      monthlyIncome,
      committedSpend,
      committedShare,
      overBudgetCategoryCount,
      missedRecurringCount,
      dueSoonRecurringCount,
    };
  }

  if (
    (committedShare !== null && committedShare >= 0.9) ||
    overBudgetCategoryCount >= 2 ||
    missedRecurringCount >= 1 ||
    dueSoonRecurringCount >= 3
  ) {
    return {
      level: 'high',
      title: 'Budget pressure is building',
      summary: 'This is the point to switch into practical stabilising steps before the next due date turns into another problem.',
      reasons,
      monthlyIncome,
      committedSpend,
      committedShare,
      overBudgetCategoryCount,
      missedRecurringCount,
      dueSoonRecurringCount,
    };
  }

  return {
    level: 'watch',
    title: 'Keep Survival close this month',
    summary: 'Money is tightening. Keep the Survival steps nearby so the next wobble does not turn into avoidance.',
    reasons,
    monthlyIncome,
    committedSpend,
    committedShare,
    overBudgetCategoryCount,
    missedRecurringCount,
    dueSoonRecurringCount,
  };
}

export function assessSurvivalActivation({
  budgetPressure,
  debtPressure,
}: {
  budgetPressure: BudgetPressureAssessment | null;
  debtPressure: DebtPressureAssessment | null;
}): SurvivalActivationAssessment | null {
  if (!budgetPressure && !debtPressure) return null;

  const trigger: SurvivalActivationAssessment['trigger'] =
    budgetPressure && debtPressure ? 'both' : budgetPressure ? 'budget' : 'debt';

  const reasonSet = new Set<string>();
  for (const reason of budgetPressure?.reasons || []) {
    reasonSet.add(reason);
  }
  for (const reason of debtPressure?.reasons || []) {
    reasonSet.add(reason);
  }
  const reasons = Array.from(reasonSet).slice(0, 4);

  if (budgetPressure?.level === 'critical' || debtPressure?.level === 'critical') {
    return {
      level: 'critical',
      title: 'Survival support should move to the front now',
      summary:
        trigger === 'both'
          ? 'Your budget and debt signals are both pointing to immediate pressure. Use Survival for the next stabilising steps now, not after another miss.'
          : budgetPressure?.level === 'critical'
            ? 'Your budget signals are already in survival territory. Use Survival now for the next stabilising steps.'
            : 'Your debt picture is already showing crisis markers. Use Survival now alongside regulated debt help.',
      reasons,
      trigger,
      budgetPressure,
      debtPressure,
    };
  }

  if (budgetPressure?.level === 'high' || debtPressure?.level === 'high') {
    return {
      level: 'high',
      title: 'Open Survival before this turns into another miss',
      summary:
        trigger === 'both'
          ? 'Budget and debt pressure are both building. Survival is the quickest place to regroup around essentials and holding scripts.'
          : budgetPressure?.level === 'high'
            ? 'Your budget signals are tightening. Survival can help before the next bill lands.'
            : 'Debt pressure is building. Survival can help you act before another account tips further.',
      reasons,
      trigger,
      budgetPressure,
      debtPressure,
    };
  }

  return {
    level: 'watch',
    title: 'Keep Survival nearby this month',
    summary: 'The pressure is still early, but Survival now has the right practical steps ready if the next few days get heavier.',
    reasons,
    trigger,
    budgetPressure,
    debtPressure,
  };
}

function pickDebtTarget(debts: Array<DebtStrategyInput & { balance: number }>, strategy: DebtStrategyName) {
  const activeDebts = debts.filter((debt) => debt.balance > 0.009);
  if (activeDebts.length === 0) return null;

  return [...activeDebts].sort((left, right) => {
    if (strategy === 'avalanche') {
      const aprGap = (right.apr || 0) - (left.apr || 0);
      if (aprGap !== 0) return aprGap;
      return right.balance - left.balance;
    }

    const balanceGap = left.balance - right.balance;
    if (balanceGap !== 0) return balanceGap;
    return (right.apr || 0) - (left.apr || 0);
  })[0] || null;
}

export function calculateDebtStrategy(
  debts: DebtStrategyInput[],
  extraPayment: number,
  strategy: DebtStrategyName,
): DebtStrategyResult {
  const activeDebts = debts
    .filter((debt) => debt.balance > 0)
    .map((debt) => ({
      ...debt,
      balance: Math.max(debt.balance, 0),
      apr: Math.max(debt.apr || 0, 0),
      minimumPayment: Math.max(debt.minimumPayment || 0, 0),
    }));

  const monthlyBudget = activeDebts.reduce((sum, debt) => sum + debt.minimumPayment, 0) + Math.max(extraPayment, 0);

  if (activeDebts.length === 0) {
    return {
      strategy,
      monthlyBudget,
      totalInterestPaid: 0,
      monthsToClear: 0,
    };
  }

  if (monthlyBudget <= 0) {
    return {
      strategy,
      monthlyBudget,
      totalInterestPaid: 0,
      monthsToClear: null,
    };
  }

  let months = 0;
  let totalInterestPaid = 0;

  while (months < 600 && activeDebts.some((debt) => debt.balance > 0.009)) {
    months += 1;

    for (const debt of activeDebts) {
      if (debt.balance <= 0.009) continue;
      const monthlyRate = (debt.apr || 0) / 100 / 12;
      const interest = debt.balance * monthlyRate;
      debt.balance += interest;
      totalInterestPaid += interest;
    }

    let remainingBudget = monthlyBudget;
    const firstTarget = pickDebtTarget(activeDebts, strategy);

    for (const debt of activeDebts) {
      if (debt.balance <= 0.009 || debt.id === firstTarget?.id) continue;
      const payment = Math.min(debt.balance, debt.minimumPayment);
      debt.balance -= payment;
      remainingBudget -= payment;
    }

    if (firstTarget) {
      const minimumPayment = Math.min(firstTarget.balance, firstTarget.minimumPayment);
      firstTarget.balance -= minimumPayment;
      remainingBudget -= minimumPayment;
    }

    while (remainingBudget > 0.009) {
      const target = pickDebtTarget(activeDebts, strategy);
      if (!target) break;

      const payment = Math.min(target.balance, remainingBudget);
      target.balance -= payment;
      remainingBudget -= payment;
    }
  }

  return {
    strategy,
    monthlyBudget,
    totalInterestPaid,
    monthsToClear: activeDebts.some((debt) => debt.balance > 0.009) ? null : months,
  };
}

export function compareDebtStrategies(
  debts: DebtStrategyInput[],
  extraPayment: number,
): DebtStrategyComparison {
  const avalanche = calculateDebtStrategy(debts, extraPayment, 'avalanche');
  const snowball = calculateDebtStrategy(debts, extraPayment, 'snowball');

  const interestDelta = Math.abs(snowball.totalInterestPaid - avalanche.totalInterestPaid);
  const monthsDelta =
    avalanche.monthsToClear !== null && snowball.monthsToClear !== null
      ? Math.abs(snowball.monthsToClear - avalanche.monthsToClear)
      : 0;

  let winner: DebtStrategyComparison['winner'] = 'tie';
  if (avalanche.monthsToClear !== null && snowball.monthsToClear !== null) {
    if (
      avalanche.totalInterestPaid < snowball.totalInterestPaid - 0.01 ||
      avalanche.monthsToClear < snowball.monthsToClear
    ) {
      winner = 'avalanche';
    } else if (
      snowball.totalInterestPaid < avalanche.totalInterestPaid - 0.01 ||
      snowball.monthsToClear < avalanche.monthsToClear
    ) {
      winner = 'snowball';
    }
  } else if (avalanche.monthsToClear !== null) {
    winner = 'avalanche';
  } else if (snowball.monthsToClear !== null) {
    winner = 'snowball';
  }

  return {
    avalanche,
    snowball,
    winner,
    interestDelta,
    monthsDelta,
  };
}

export function calculateDebtModel(balance: number, apr: number | null, monthlyPayment: number, extraPayment = 0): DebtModel {
  const safeBalance = Math.max(balance, 0);
  const safeApr = Math.max(apr || 0, 0);
  const safePayment = Math.max(monthlyPayment + extraPayment, 0);
  const monthlyRate = safeApr / 100 / 12;
  const monthlyInterest = safeBalance * monthlyRate;
  const annualInterest = safeBalance * (safeApr / 100);

  if (safePayment <= monthlyInterest || safePayment === 0) {
    return { monthlyInterest, annualInterest, estimatedMonthsToClear: null };
  }

  let remaining = safeBalance;
  let months = 0;
  while (remaining > 0 && months < 600) {
    remaining += remaining * monthlyRate;
    remaining -= safePayment;
    months += 1;
  }

  return {
    monthlyInterest,
    annualInterest,
    estimatedMonthsToClear: remaining > 0 ? null : months,
  };
}
